import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FilePlus2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { CASE_STATUS, PURPOSE_LABELS, StatusBadge, canOrder } from "@/lib/cases";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/app/AppShell";
import { Input } from "@/components/ui/input";

export default function CasesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases?${new URLSearchParams({ q, status })}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const actions = canOrder(user) && (
    <Link to="/console/cases/new" data-testid="cases-new-button"
      className="inline-flex items-center gap-2 rounded-full bg-oath-ink px-5 py-2.5 font-mono-label text-[11px] font-semibold text-white hover:bg-black">
      <FilePlus2 className="h-4 w-4" /> NEW VERIFICATION
    </Link>
  );

  return (
    <AppShell eyebrow={`— ${(user.org_name || "").toUpperCase()}`} title="Cases" actions={actions}>
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-oath-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search case #, name, reference or city" className="h-10 bg-white pl-9" data-testid="cases-search-input" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm" data-testid="cases-status-filter">
          <option value="">All statuses</option>
          {Object.entries(CASE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>
      <div className="mt-5 overflow-x-auto rounded-2xl border border-gray-200 bg-white">
        <table className="w-full min-w-[820px] text-left text-sm" data-testid="cases-table">
          <thead className="border-b border-gray-200 bg-oath-bg/60 font-mono-label text-[9px] text-oath-muted">
            <tr><th className="px-4 py-3">CASE</th><th className="px-4 py-3">SUBJECT</th><th className="px-4 py-3">ADDRESS</th>
              <th className="px-4 py-3">PURPOSE</th><th className="px-4 py-3">OPENED</th><th className="px-4 py-3">STATUS</th></tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={6} className="px-4 py-8"><Loader2 className="h-5 w-5 animate-spin text-oath-muted" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-oath-muted" data-testid="cases-empty">No cases match.</td></tr>}
            {rows?.map((c) => (
              <tr key={c.id} onClick={() => navigate(`/console/cases/${c.id}`)} className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-oath-bg/50" data-testid={`case-row-${c.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-oath-ink">{c.case_number}{c.priority === "rush" && <span className="ml-2 rounded bg-red-50 px-1.5 text-[10px] text-red-600">RUSH</span>}</td>
                <td className="px-4 py-3 font-medium text-oath-ink">{c.subject.first_name} {c.subject.last_name}</td>
                <td className="px-4 py-3 text-oath-secondary">{c.address.city}, {c.address.state}</td>
                <td className="px-4 py-3 text-oath-secondary">{PURPOSE_LABELS[c.permissible_purpose]}</td>
                <td className="px-4 py-3 font-mono text-xs text-oath-secondary">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} testid={`case-row-status-${c.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
